import { Button, Divider, Slider } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { formatNumber } from 'util/Funcs';
import DefaultDropDown from './DefaultDropDown';

interface StepDefinition {
    max: number;
    step: number;
}

interface RangeFilterProps {
    title: string;
    value: [number, number];
    limit: [number, number];
    steps: StepDefinition[]; // Dynamically define steps per filter
    unit: string;
    onChanged: (value: [number, number]) => void;
    'data-cy'?: string;
    disabled?: boolean;
    disabledText?: string;
}

// Function to generate step values dynamically based on provided step definitions
const generateStepValues = (steps: StepDefinition[]) => {
    let values: number[] = [0]; // Start from 0
    let current = 0;

    for (const range of steps) {
        while (current + range.step <= range.max) {
            current += range.step;
            values.push(current);
        }
    }

    return values;
};

const mapSliderToValue = (sliderIndex: number, stepValues: number[]) => stepValues[sliderIndex];

const mapValueToSlider = (actualValue: number, stepValues: number[]) => {
    const index = stepValues.findIndex((val) => val >= actualValue);
    return index === -1 ? stepValues.length - 1 : index;
};

const RangeFilter = (props: RangeFilterProps) => {
    const { title, unit, value, steps, onChanged, 'data-cy': dataCy, disabled = false, disabledText = '0' } = props;

    const STEP_VALUES = generateStepValues(steps);

    const [rawRange, setRawRange] = useState<[number, number]>(value);

    const sliderRange = [mapValueToSlider(rawRange[0], STEP_VALUES), mapValueToSlider(rawRange[1], STEP_VALUES)] as [number, number];

    const childRef = useRef<{ closePopup: () => void }>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [headerTitle, setHeaderTitle] = useState<string>(title);

    useEffect(() => {
        setRawRange(value);

        let caption = title;
        if (disabled) {
            caption = disabledText;
        } else if (value[0] !== 0 || value[1] !== 0) {
            if (value[0] !== 0 && value[1] === 0) {
                caption = `> ${formatNumber(value[0])}${unit}`;
            } else if (value[0] === 0 && value[1] !== 0) {
                caption = `< ${formatNumber(value[1])}${unit}`;
            } else {
                caption = `${formatNumber(value[0])}${unit} ~ ${formatNumber(value[1])}${unit}`;
            }
        }
        setHeaderTitle(caption);
    }, [value, title, unit, disabled, disabledText]);

    const handleSliderChange = (_: Event, newValue: number | number[]) => {
        if (Array.isArray(newValue)) {
            const newSliderRange: [number, number] = [newValue[0], newValue[1]];
            const actualMin = mapSliderToValue(newSliderRange[0], STEP_VALUES);
            const actualMax = mapSliderToValue(newSliderRange[1], STEP_VALUES);
            setRawRange([actualMin, actualMax]);
        }
        setErrorMessage(null);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        const cleaned = e.target.value.replace(/\./g, '');
        const val = Number(cleaned) || 0;

        setRawRange((prev) => {
            const newValue: [number, number] = idx === 0 ? [val, prev[1]] : [prev[0], val];
            return newValue;
        });
        setErrorMessage(null);
    };

    const onApplyClicked = () => {
        if (rawRange[1] !== 0 && rawRange[0] > rawRange[1]) {
            setErrorMessage('Minimum value cannot be greater than Maximum.');
            return;
        }

        onChanged(rawRange);

        if (childRef.current) {
            childRef.current.closePopup();
        }
    };

    const onClearClicked = () => {
        setRawRange([0, 0]);
        onChanged([0, 0]);
    };

    return (
        <DefaultDropDown
            ref={childRef}
            Title={<div className="truncate">{headerTitle}</div>}
            IsEmpty={rawRange[0] + rawRange[1] === 0}
            onClearClicked={onClearClicked}
        >
            <div className="flex flex-col gap-[20px] text-[12px] mt-[5px]">
                <div className="h-[60px] flex flex-col justify-between px-[20px]">
                    <div className="text-[20px] font-semibold">{title}</div>
                    <div className="font-medium text-[14px]">{'Select Range'}</div>
                </div>
                <div className="px-[20px]">
                    <Slider
                        getAriaLabel={() => `${title} range`}
                        value={sliderRange}
                        onChange={handleSliderChange}
                        getAriaValueText={(v) => `${formatNumber(mapSliderToValue(v, STEP_VALUES))}${unit}`}
                        max={STEP_VALUES.length - 1}
                        min={0}
                        size="small"
                        step={1}
                        valueLabelDisplay="on"
                        valueLabelFormat={(v) => `${formatNumber(mapSliderToValue(v, STEP_VALUES))}${unit}`}
                        slotProps={{
                            valueLabel: {
                                className: 'bg-black',
                            },
                        }}
                        data-cy={dataCy}
                    />
                </div>

                <div className="grid grid-cols-2 gap-[26px] px-[20px]">
                    <div>
                        <div className="font-medium pb-[8px]">Minimum</div>
                        <div className="flex gap-[5px] justify-between border border-my-gray rounded-[5px] h-[36px] items-center px-[5px]">
                            <input
                                type="text"
                                className="w-full px-[5px]"
                                value={formatNumber(rawRange[0])}
                                onChange={(e) => onInputChange(e, 0)}
                                data-cy="min-input"
                            />
                            <span className="font-bold">{unit}</span>
                        </div>
                    </div>

                    <div>
                        <div className="font-medium pb-[8px]">Maximum</div>
                        <div className="flex gap-[5px] justify-between border border-my-gray rounded-[5px] h-[36px] items-center px-[5px]">
                            <input
                                type="text"
                                className="w-full px-[5px]"
                                value={formatNumber(rawRange[1])}
                                onChange={(e) => onInputChange(e, 1)}
                                data-cy="max-input"
                            />
                            <span className="font-bold">{unit}</span>
                        </div>
                    </div>
                </div>

                {errorMessage && <div className="text-red-500 text-[12px] mx-auto">{errorMessage}</div>}

                <Divider className="w-full" />

                <div className="pb-[16px] flex justify-between px-[20px]">
                    <Button onClick={onClearClicked} data-cy="clear-button">
                        Clear
                    </Button>
                    <Button variant="contained" color="primary" onClick={onApplyClicked} data-cy="apply-button">
                        Apply
                    </Button>
                </div>
            </div>
        </DefaultDropDown>
    );
};

export default RangeFilter;
